<template>
  <a-table
    class="table datatable thead-light dq-report-table"
    :columns="columns"
    :data-source="dataSource"
    :loading="loading"
    :pagination="pagination"
    @change="onChange"
    row-key="key"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'Employee'">
        <div class="d-flex align-items-center">
          <div class="ms-0">
            <p class="text-dark mb-0">
              {{ record.employee_name }}
            </p>
            <span class="fs-12">
              {{ record.employee_id }} •
              <span v-if="record.designation">{{ record.designation }}</span>
              <span v-if="record.department"> • {{ record.department }}</span>
            </span>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'Period'">
        {{ record.period_label || record.period }}
      </template>

      <template v-else-if="moneyColumns.includes(column.key)">
        {{ currency }}
        {{
          Number(record[column.dataIndex] || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })
        }}
      </template>
    </template>
  </a-table>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  currency: { type: String, default: "KES" },
  total: { type: Number, default: 0 },
  pageSize: { type: Number, default: 25 },
  current: { type: Number, default: 1 },
});
const emit = defineEmits(["change"]);

const columns = [
  {
    title: "Period",
    dataIndex: "period_label",
    key: "Period",
    sorter: (a, b) =>
      (a.period_label || "").localeCompare(b.period_label || ""),
  },
  {
    title: "Employee",
    dataIndex: "employee_name",
    key: "Employee",
    sorter: (a, b) =>
      (a.employee_name || "").localeCompare(b.employee_name || ""),
  },
  {
    title: "SACCO Loan",
    dataIndex: "sacco_loan",
    key: "SACCO",
    sorter: (a, b) => (a.sacco_loan || 0) - (b.sacco_loan || 0),
  },
  {
    title: "HELB",
    dataIndex: "helb",
    key: "HELB",
    sorter: (a, b) => (a.helb || 0) - (b.helb || 0),
  },
  {
    title: "Mortgage",
    dataIndex: "mortgage_interest",
    key: "Mortgage",
    sorter: (a, b) => (a.mortgage_interest || 0) - (b.mortgage_interest || 0),
  },
  {
    title: "Pension",
    dataIndex: "pension_contrib",
    key: "Pension",
    sorter: (a, b) => (a.pension_contrib || 0) - (b.pension_contrib || 0),
  },
  {
    title: "Insurance",
    dataIndex: "insurance_premium",
    key: "Insurance",
    sorter: (a, b) => (a.insurance_premium || 0) - (b.insurance_premium || 0),
  },
  {
    title: "Total Loans",
    dataIndex: "loan_total",
    key: "LoanTotal",
    sorter: (a, b) => (a.loan_total || 0) - (b.loan_total || 0),
  },
  {
    title: "Total Savings",
    dataIndex: "savings_total",
    key: "SavingsTotal",
    sorter: (a, b) => (a.savings_total || 0) - (b.savings_total || 0),
  },
  {
    title: "Total Deductions",
    dataIndex: "total_deductions",
    key: "DeductionsTotal",
    sorter: (a, b) => (a.total_deductions || 0) - (b.total_deductions || 0),
  },
];

const moneyColumns = [
  "SACCO",
  "HELB",
  "Mortgage",
  "Pension",
  "Insurance",
  "LoanTotal",
  "SavingsTotal",
  "DeductionsTotal",
];

const dataSource = computed(() =>
  (props.rows || []).map((r, idx) => ({
    key: r.id || idx,
    period: r.period,
    period_label: r.period_label,
    employee_id: r.employee_id,
    employee_name: r.employee_name,
    department: r.department,
    designation: r.designation,
    sacco_loan: r.sacco_loan,
    helb: r.helb,
    mortgage_interest: r.mortgage_interest,
    pension_contrib: r.pension_contrib,
    insurance_premium: r.insurance_premium,
    loan_total: r.loan_total,
    savings_total: r.savings_total,
    total_deductions: r.total_deductions,
  }))
);

const pagination = computed(() => ({
  total: props.total,
  pageSize: props.pageSize,
  current: props.current,
  showTotal: (t) => `Total ${t} items`,
}));

function onChange(pagination, filters, sorter) {
  emit("change", pagination, filters, sorter);
}
</script>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-report-table .ant-table) {
    min-width: 1180px;
  }

  :deep(.dq-report-table .ant-table-cell) {
    vertical-align: top;
    white-space: normal;
  }
}
</style>
